import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { TbArrowRight } from 'react-icons/tb';
import { formatBalanceWithSign } from '../../../utils/bankAccountUtil';
import { Currency } from '../../../types/bankAccount';
import connectionService from '../../../services/community/connectionService';
import * as bankAccountService from '../../../services/community/bankAccountService';
import { Connection, ConnectionType } from '../../../redux/slices/connectionsSlice';

interface MemberData {
  name: string;
  walletBalance: number;
}

function MemberBalanceCards(): JSX.Element {
  const { t, i18n } = useTranslation('', {
    keyPrefix: 'page.financial.overview.memberBalanceCards',
  });
  const [connections, setConnections] = useState<Connection[]>([]);
  const [walletBalances, setWalletBalances] = useState<Record<number, number>>({});
  const [selectedCurrency] = useState<Currency>(Currency.EUR);

  useEffect(() => {
    connectionService
      .getConnections()
      .then((conns) => {
        const filteredConnections = conns.filter(
          (c: Connection) =>
            c.type === ConnectionType.MEMBER || c.type === ConnectionType.EXTERNAL_MEMBER,
        );
        setConnections(filteredConnections);

        const walletBalancePromises = filteredConnections.map(async (c: Connection) => {
          try {
            const balance = await bankAccountService.getMemberWalletBalance(
              +c.dashboardMemberId,
              selectedCurrency,
            );
            return { memberId: +c.dashboardMemberId, balance: balance.balance };
          } catch (error) {
            console.error(`Error fetching wallet balance for member ${c.dashboardMemberId}:`, error);
            return { memberId: +c.dashboardMemberId, balance: 0 };
          }
        });

        return Promise.all(walletBalancePromises).then((results) => {
          const walletBalancesMap: Record<number, number> = {};
          results.forEach(({ memberId, balance }) => {
            walletBalancesMap[memberId] = balance;
          });
          setWalletBalances(walletBalancesMap);
        });
      })
      .catch(() => {
        setConnections([]);
        setWalletBalances({});
      });
  }, [selectedCurrency]);

  const members = useMemo((): MemberData[] => {
    if (Object.keys(walletBalances).length === 0 || connections.length === 0) return [];

    return connections
      .map((c) => {
        const memberId = +c.dashboardMemberId;
        const name = `${c.fields.FIRST_NAME} ${c.fields.LAST_NAME}`.trim();
        const walletBalance = walletBalances[memberId] ?? 0;
        return { name, walletBalance };
      })
      .sort((a, b) => b.walletBalance - a.walletBalance);
  }, [walletBalances, connections]);

  const topFiveMembers = members.slice(0, 5);
  const bottomFiveMembers = members.slice(-5).reverse();

  const renderMemberRow = (member: MemberData, index: number) => (
    <tr key={index} className="border-b border-gray-100 hover:bg-secondary-200 hover:bg-opacity-30">
      <td className="px-4 py-3 text-sm font-medium text-primary-900">{member.name}</td>
      <td className="px-4 py-3 text-right text-sm text-primary-900">
        {formatBalanceWithSign(member.walletBalance, selectedCurrency, i18n.language, false)}
      </td>
    </tr>
  );

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
      {/* Top 5 Members */}
      <div className="rounded-xl bg-secondary-200 bg-opacity-50 p-6">
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-primary-900">{t('topFive.title')}</h2>
          <Link
            to="/financial/reports"
            state={{ activeTab: 'members' }}
            className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-primary-900 transition-colors hover:bg-primary-300 hover:bg-opacity-20">
            <span>{t('seeAll')}</span>
            <TbArrowRight size={16} />
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="px-4 py-3 text-left text-sm font-semibold text-primary-900">
                  {t('table.memberName')}
                </th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-primary-900">
                  {t('table.wallet')}
                </th>
              </tr>
            </thead>
            <tbody>{topFiveMembers.map((member, index) => renderMemberRow(member, index))}</tbody>
          </table>
        </div>
      </div>

      {/* Bottom 5 Members */}
      <div className="rounded-xl bg-secondary-200 bg-opacity-50 p-6">
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-primary-900">{t('bottomFive.title')}</h2>
          <Link
            to="/financial/reports"
            state={{ activeTab: 'members' }}
            className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-primary-900 transition-colors hover:bg-primary-300 hover:bg-opacity-20">
            <span>{t('seeAll')}</span>
            <TbArrowRight size={16} />
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="px-4 py-3 text-left text-sm font-semibold text-primary-900">
                  {t('table.memberName')}
                </th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-primary-900">
                  {t('table.wallet')}
                </th>
              </tr>
            </thead>
            <tbody>
              {bottomFiveMembers.map((member, index) => renderMemberRow(member, index))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default MemberBalanceCards;
